import { authenticatedFetch, getAuthHeaders } from './authApi';
import { getEnv } from '../utils/env';
import { invalidateCachedValue } from './requestCache';
import { notifyOtherMatchTabs } from '../utils/matchRevision';

type UnknownRecord = Record<string, unknown>;
const MAX_MATCHES_LIMIT = 500;
export const STATISTICS_REFRESH_EVENT = 'dohockey:statistics-refresh';
export const MATCHES_CHANGED_EVENT = 'dohockey:matches-changed';
export const LIVE_MATCH_EVENTS_CHANGED_EVENT = 'dohockey:live-match-events-changed';

const notifyMatchesChanged = (): void => {
  invalidateCachedValue('dashboard:matches');
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(MATCHES_CHANGED_EVENT));
  }
};

const notifyLiveMatchEventsChanged = (matchId: string): void => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(LIVE_MATCH_EVENTS_CHANGED_EVENT, {
      detail: { matchId },
    }));
  }
};

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const extractMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object' && 'detail' in payload) {
    const detail = (payload as UnknownRecord).detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
      const message = (detail as UnknownRecord).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
    if (Array.isArray(detail)) {
      const messages = detail.map(item => item && typeof item.msg === 'string' ? item.msg : '')
        .filter(Boolean);
      if (messages.length) return messages.join('; ');
    }
  }
  if (payload && typeof payload === 'object' && 'error' in (payload as UnknownRecord)) {
    const error = (payload as UnknownRecord).error;
    if (typeof error === 'string' && error.trim().length > 0) return error;
    if (error && typeof error === 'object' && 'message' in (error as UnknownRecord)) {
      const message = (error as UnknownRecord).message;
      if (typeof message === 'string' && message.trim().length > 0) return message;
    }
  }
  if (payload && typeof payload === 'object' && 'message' in (payload as UnknownRecord)) {
    const message = (payload as UnknownRecord).message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return fallback;
};

const extractData = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in (payload as UnknownRecord)) {
    return (payload as UnknownRecord).data;
  }
  return payload;
};

const matchesRequest = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const response = await authenticatedFetch(`${baseUrl}${path}`, {
    ...options,
    credentials: 'include',
    headers: getAuthHeaders(options.headers || {}),
  });

  if (response.status === 204) return null;

  const payload = await parseJson(response);
  if (!response.ok) {
    const detail = payload && typeof payload === 'object' ? (payload as UnknownRecord).detail : null;
    const code = detail && typeof detail === 'object' ? (detail as UnknownRecord).code : undefined;
    throw Object.assign(new Error(extractMessage(payload, `Matches API request failed (${response.status})`)), {
      code: typeof code === 'string' ? code : undefined,
      reason: detail && typeof detail === 'object' && typeof (detail as UnknownRecord).reason === 'string' ? (detail as UnknownRecord).reason : undefined,
    });
  }
  if (
    payload &&
    typeof payload === 'object' &&
    'ok' in (payload as UnknownRecord) &&
    (payload as UnknownRecord).ok === false
  ) {
    throw new Error(extractMessage(payload, 'Matches API request failed'));
  }

  const refresh = (payload as UnknownRecord | null)?.statistics_refresh;
  if (refresh && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(STATISTICS_REFRESH_EVENT, { detail: refresh }));
  }
  return extractData(payload);
};

export const listMatchesApi = async (params?: {
  team_id?: string;
  club_id?: string;
  from?: string;
  to?: string;
  has_lineup?: boolean;
  limit?: number;
  offset?: number;
}): Promise<unknown[]> => {
  const query = new URLSearchParams();
  if (params?.team_id) query.set('team_id', params.team_id);
  if (params?.club_id) query.set('club_id', params.club_id);
  if (params?.from) query.set('from', params.from);
  if (params?.to) query.set('to', params.to);
  if (typeof params?.has_lineup === 'boolean') query.set('has_lineup', String(params.has_lineup));
  if (typeof params?.limit === 'number') query.set('limit', String(Math.min(MAX_MATCHES_LIMIT, Math.max(1, params.limit))));
  if (typeof params?.offset === 'number') query.set('offset', String(Math.max(0, params.offset)));
  const qs = query.toString();
  const payload = await matchesRequest(`/api/v1/matches${qs ? `?${qs}` : ''}`, { method: 'GET' });
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord;
    if (Array.isArray(record.items)) return record.items as unknown[];
    if (Array.isArray(record.matches)) return record.matches as unknown[];
    if (Array.isArray(record.results)) return record.results as unknown[];
  }
  return [];
};

export const listPlayerMatchHistoryApi = async (clubId: string, limit: number, offset: number): Promise<UnknownRecord[]> => {
  const query = new URLSearchParams({ club_id: clubId, limit: String(limit), offset: String(offset) });
  const data = await matchesRequest(`/api/v1/matches/player-history?${query}`);
  if (!Array.isArray(data)) throw new Error('Invalid player match history response');
  return data as UnknownRecord[];
};

export const listMatchOverviewApi = async (
  teamIds: string | readonly string[],
  clubId: string,
  page?: { from: string; limit: number; offset: number },
): Promise<UnknownRecord[]> => {
  const query = new URLSearchParams({ club_id: clubId });
  for (const teamId of typeof teamIds === 'string' ? [teamIds] : teamIds) query.append('team_id', teamId);
  if (page) {
    query.set('from', page.from);
    query.set('limit', String(page.limit));
    query.set('offset', String(page.offset));
  }
  const data = await matchesRequest(`/api/v1/matches/overview?${query}`);
  if (!Array.isArray(data)) throw new Error('Invalid match overview response');
  return data as UnknownRecord[];
};

export const listClubMatchCountsApi = async (clubId: string): Promise<{
  team_id: string;
  status: string | null;
  count: number;
}[]> => {
  const query = new URLSearchParams({ club_id: clubId });
  const data = await matchesRequest(`/api/v1/matches/counts?${query}`);
  if (!Array.isArray(data)) throw new Error('Invalid match counts response');
  return data;
};

export const getMatchApi = async (id: string): Promise<unknown | null> => {
  const payload = await matchesRequest(`/api/v1/matches/${encodeURIComponent(id)}`, { method: 'GET', cache: 'no-store' });
  return payload ?? null;
};

export const getMatchRevisionApi = (id: string): Promise<unknown> =>
  matchesRequest(`/api/v1/matches/${encodeURIComponent(id)}/revision`, { cache: 'no-store' });

export const createMatchApi = async (payload: Record<string, unknown>): Promise<unknown> => {
  const result = await matchesRequest('/api/v1/matches', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  notifyMatchesChanged();
  return result;
};

export const updateMatchApi = async (id: string, payload: Record<string, unknown>): Promise<unknown> => {
  const result = await matchesRequest(`/api/v1/matches/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  notifyMatchesChanged();
  notifyOtherMatchTabs(id);
  return result;
};

export const deleteMatchApi = async (id: string): Promise<void> => {
  await matchesRequest(`/api/v1/matches/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  notifyMatchesChanged();
  notifyOtherMatchTabs(id);
};

export const getMatchesWithLiveEventsApi = async (matchIds: string[]): Promise<Record<string, boolean>> => {
  const payload = await matchesRequest('/api/v1/matches/has-live-events', {
    method: 'POST',
    body: JSON.stringify({ match_ids: matchIds }),
  });
  if (!payload || typeof payload !== 'object') return {};
  if ('data' in (payload as UnknownRecord) && typeof (payload as UnknownRecord).data === 'object') {
    return getMatchesWithLiveEventsApiFromData((payload as UnknownRecord).data);
  }
  return getMatchesWithLiveEventsApiFromData(payload);
};

const getMatchesWithLiveEventsApiFromData = (payload: unknown): Record<string, boolean> => {
  if (!payload || typeof payload !== 'object') return {};
  const result: Record<string, boolean> = {};
  Object.entries(payload as UnknownRecord).forEach(([key, value]) => {
    if (typeof value === 'boolean') result[key] = value;
  });
  return result;
};

export const listLiveMatchEventsApi = async (matchId: string, limit = 20, includeAll = false): Promise<unknown[]> => {
  const safeLimit = Math.min(100, Math.max(1, Math.floor(limit)));
  const payload = await matchesRequest(`/api/v1/live/matches/${encodeURIComponent(matchId)}/events?limit=${safeLimit}${includeAll ? '&include_all=true' : ''}`, {
    method: 'GET',
  });
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord;
    if (Array.isArray(record.items)) return record.items as unknown[];
    if (Array.isArray(record.events)) return record.events as unknown[];
    if (Array.isArray(record.results)) return record.results as unknown[];
  }
  return [];
};

export const createLiveMatchEventApi = async (matchId: string, payload: Record<string, unknown>): Promise<unknown> => {
  const result = await matchesRequest(`/api/v1/live/matches/${encodeURIComponent(matchId)}/events`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  notifyLiveMatchEventsChanged(matchId);
  return result;
};

export const resetLiveMatchApi = async (matchId: string): Promise<unknown> => {
  const result = await matchesRequest(`/api/v1/live/matches/${encodeURIComponent(matchId)}/reset`, { method: 'POST' });
  notifyMatchesChanged();
  notifyOtherMatchTabs(matchId);
  notifyLiveMatchEventsChanged(matchId);
  return result;
};

export const resetLiveMatchEventsApi = async (matchId: string): Promise<unknown> => {
  const result = await matchesRequest(`/api/v1/live/matches/${encodeURIComponent(matchId)}/events`, {
    method: 'DELETE',
  });
  notifyLiveMatchEventsChanged(matchId);
  return result;
};

export const updateLiveMatchEventApi = async (
  matchId: string,
  eventId: string,
  payload: Record<string, unknown>
): Promise<unknown> => {
  const result = await matchesRequest(
    `/api/v1/live/matches/${encodeURIComponent(matchId)}/events/${encodeURIComponent(eventId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }
  );
  notifyLiveMatchEventsChanged(matchId);
  return result;
};

export const deleteLiveMatchEventApi = async (matchId: string, eventId: string, recalculateScore = false): Promise<unknown> => {
  const result = await matchesRequest(
    `/api/v1/live/matches/${encodeURIComponent(matchId)}/events/${encodeURIComponent(eventId)}${recalculateScore ? '?recalculateScore=true' : ''}`,
    {
      method: 'DELETE',
    }
  );
  notifyLiveMatchEventsChanged(matchId);
  return result;
};

export const getLiveMatchApi = async (identifier: string): Promise<unknown | null> => {
  const payload = await matchesRequest(`/api/v1/live/matches/${encodeURIComponent(identifier)}`, {
    method: 'GET',
  });
  return payload ?? null;
};

export const updateLiveMatchApi = async (identifier: string, payload: Record<string, unknown>): Promise<unknown> => {
  return await matchesRequest(`/api/v1/live/matches/${encodeURIComponent(identifier)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
};

export const getStatisticsRefreshStatusApi = (clubId: string): Promise<unknown> =>
  matchesRequest(`/api/v1/matches/statistics-refresh-status?club_id=${encodeURIComponent(clubId)}`);

export interface AutomaticLineupProposal {
  lineup: Record<string, string>;
  algorithm: string;
  history_enabled?: boolean;
  history_known: number;
  history_unknown: number;
  non_preferred_positions: string[];
  saved: false;
}

export const proposeAutomaticLineupApi = async (matchId: string): Promise<AutomaticLineupProposal> => {
  const payload = await matchesRequest(`/api/v1/matches/${encodeURIComponent(matchId)}/automatic-lineup`, { method: 'POST' });
  return extractData(payload) as AutomaticLineupProposal;
};
